/**
 * PHARMAKON — Fournisseur / Laboratoire B2B Registration
 *
 * 5-step onboarding flow:
 *   Step 1 — Type selector      : Fournisseur vs Laboratoire
 *   Step 2 — Company identity   : raison_sociale, wilaya, adresse, téléphone, RC, agrément, RIB, NIF
 *   Step 3 — Admin account      : prenom, nom, email, password
 *   Step 4 — Documents upload   : RC PDF, Agrément PDF, RIB PDF, Carte Fiscale PDF
 *   Step 5 — Confirmation + submit
 *
 * POST /api/v1/auth/register-fournisseur (multipart/form-data)
 * Account created with statut_compte = EN_ATTENTE → pending admin review.
 *
 * FIX: Step sub-components (Step1…Step5) were defined INSIDE the parent function body.
 * React treats them as new component types on every keystroke re-render → unmount/remount → focus lost.
 * Solution: inline all step JSX directly in the return, guarded by `step === N` checks.
 */
import { useState, useRef, useMemo } from "react";
import { useNavigate, Link } from "react-router-dom";
import { T, BASE_CSS, ICONS } from "../../config/theme.js";
import { authApi } from "../../services/index.js";
import { useThemeStore, isDarkTheme } from "../../stores/themeStore.js";

// =============================================================================
// CONFIG
// =============================================================================
const CFG = {
  WILAYAS: [
    "Adrar",
    "Chlef",
    "Laghouat",
    "Oum El Bouaghi",
    "Batna",
    "Béjaïa",
    "Biskra",
    "Béchar",
    "Blida",
    "Bouira",
    "Tamanrasset",
    "Tébessa",
    "Tlemcen",
    "Tiaret",
    "Tizi Ouzou",
    "Alger",
    "Djelfa",
    "Jijel",
    "Sétif",
    "Saïda",
    "Skikda",
    "Sidi Bel Abbès",
    "Annaba",
    "Guelma",
    "Constantine",
    "Médéa",
    "Mostaganem",
    "MSila",
    "Mascara",
    "Ouargla",
    "Oran",
    "El Bayadh",
    "Illizi",
    "Bordj Bou Arréridj",
    "Boumerdès",
    "El Tarf",
    "Tindouf",
    "Tissemsilt",
    "El Oued",
    "Khenchela",
    "Souk Ahras",
    "Tipaza",
    "Mila",
    "Aïn Defla",
    "Naâma",
    "Aïn Témouchent",
    "Ghardaïa",
    "Relizane",
    "Timimoun",
    "Bordj Badji Mokhtar",
    "Ouled Djellal",
    "Béni Abbès",
    "In Salah",
    "In Guezzam",
    "Touggourt",
    "Djanet",
    "El MGhair",
    "El Menia",
  ],
  ACCOUNT_TYPES: [
    {
      id: "FOURNISSEUR",
      label: "Grossiste / Distributeur",
      icon: "🚚",
      desc: "Distribution pharmaceutique, vente en gros aux pharmacies et cliniques",
      color: "#00D264",
    },
    {
      id: "LABORATOIRE",
      label: "Laboratoire Pharmaceutique",
      icon: "🔬",
      desc: "Fabricant ou importateur — accès Data Synthèse et analytics marché",
      color: "#7c3aed",
      badge: "Premium",
    },
  ],
  REQUIRED_DOCS: [
    {
      key: "registre_commerce",
      label: "Registre de Commerce",
      hint: "Extrait de registre de commerce à jour (moins de 3 mois)",
      required: true,
      icon: "🏢",
    },
    {
      key: "agrement",
      label: "Agrément Distribution Pharmaceutique",
      hint: "Agrément délivré par le Ministère de la Santé — obligatoire",
      required: true,
      icon: "📋",
    },
    {
      key: "rib",
      label: "Relevé d'Identité Bancaire (RIB)",
      hint: "Pour la gestion des paiements et virements",
      required: false,
      icon: "🏦",
    },
    {
      key: "carte_fiscale",
      label: "Carte Fiscale (NIF)",
      hint: "Numéro d'identification fiscal — recommandé",
      required: false,
      icon: "📄",
    },
  ],
  STEPS: [
    {
      id: 1,
      label: "Type",
      icon: "M19 11H5m14 0a2 2 0 012 2v6a2 2 0 01-2 2H5a2 2 0 01-2-2v-6a2 2 0 012-2m14 0V9a2 2 0 00-2-2M5 11V9a2 2 0 012-2m0 0V5a2 2 0 012-2h6a2 2 0 012 2v2M7 7h10",
    },
    {
      id: 2,
      label: "Entreprise",
      icon: "M3 21h18M3 7v1a3 3 0 006 0V7m0 1a3 3 0 006 0V7m0 1a3 3 0 006 0V7M3 7l9-4 9 4M4 10h1m5 0h1m5 0h1M4 14h1m5 0h1m5 0h1M4 18h1m5 0h1m5 0h1",
    },
    {
      id: 3,
      label: "Compte",
      icon: "M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z",
    },
    {
      id: 4,
      label: "Documents",
      icon: "M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z",
    },
    { id: 5, label: "Validation", icon: ICONS.check },
  ],
};

// =============================================================================
// STYLES
// =============================================================================
const css = (c) =>
  BASE_CSS(c) +
  `
.rf-shell{min-height:100vh;background:${c.bg};display:flex;flex-direction:column;}
.rf-top{padding:18px 32px;display:flex;align-items:center;justify-content:space-between;border-bottom:1px solid ${c.border};background:${c.surf};}
.rf-brand{display:flex;align-items:center;gap:10px;}
.rf-body{flex:1;display:flex;gap:0;max-width:1000px;margin:0 auto;width:100%;padding:40px 24px;}
.rf-sidebar{width:200px;flex-shrink:0;padding-right:32px;}
.rf-step-item{display:flex;align-items:center;gap:10px;padding:10px 0;position:relative;}
.rf-step-item::after{content:'';position:absolute;left:14px;top:100%;width:1.5px;height:100%;background:${c.border};}
.rf-step-item:last-child::after{display:none;}
.step-dot{width:30px;height:30px;border-radius:50%;display:flex;align-items:center;justify-content:center;flex-shrink:0;font-size:11px;font-weight:700;transition:all 200ms;}
.step-dot.done{background:${c.accent};color:#fff;}
.step-dot.active{background:${c.accentDim};border:2px solid ${c.accent};color:${c.accent};}
.step-dot.todo{background:${c.raised};border:1px solid ${c.border};color:${c.t3};}
.step-lbl{font-size:12px;font-weight:600;color:${c.t3};transition:color 150ms;}
.step-lbl.active{color:${c.accent};font-weight:700;}
.step-lbl.done{color:${c.t1};}
.rf-main{flex:1;}
.rf-card{background:${c.surf};border:1px solid ${c.border};border-radius:16px;padding:32px;box-shadow:${c.shadow};}
.rf-card-title{font-size:20px;font-weight:800;color:${c.t0};margin-bottom:4px;}
.rf-card-sub{font-size:12.5px;color:${c.t2};margin-bottom:28px;}
.frow{display:grid;grid-template-columns:1fr 1fr;gap:14px;margin-bottom:14px;}
.frow.one{grid-template-columns:1fr;}
.fld{display:flex;flex-direction:column;gap:5px;}
.fld label{font-size:9.5px;font-weight:800;color:${c.t3};text-transform:uppercase;letter-spacing:.7px;}
.fld label .req{color:${c.accent};}
.inp{width:100%;padding:10px 12px;background:${c.raised};border:1px solid ${c.border};border-radius:9px;font-size:13px;color:${c.t0};font-family:'Outfit',sans-serif;outline:none;transition:all 120ms;box-sizing:border-box;}
.inp:focus{border-color:${c.accent};box-shadow:0 0 0 3px ${c.accentDim};}
.inp.err{border-color:${c.red};}
.fld-hint{font-size:10px;color:${c.t3};}
.fld-err{font-size:10px;color:${c.red};font-weight:600;}
.doc-grid{display:grid;grid-template-columns:1fr 1fr;gap:12px;margin-bottom:20px;}
.doc-card{border:2px dashed ${c.border};border-radius:12px;padding:16px;cursor:pointer;transition:all 140ms;position:relative;min-height:100px;}
.doc-card:hover{border-color:${c.accent};background:${c.accentDim};}
.doc-card.uploaded{border-style:solid;border-color:${c.accent};background:${c.accentDim};}
.doc-card.required-missing{border-color:${c.red};}
.rf-actions{display:flex;align-items:center;justify-content:space-between;margin-top:24px;gap:12px;}
.btn-back{padding:10px 20px;border-radius:10px;border:1px solid ${c.border};background:${c.raised};color:${c.t1};font-size:13px;font-weight:600;cursor:pointer;transition:all 120ms;font-family:'Outfit',sans-serif;}
.btn-next{padding:10px 22px;border-radius:10px;border:none;background:${c.accent};color:#fff;font-size:13px;font-weight:700;cursor:pointer;transition:all 120ms;font-family:'Outfit',sans-serif;display:flex;align-items:center;gap:7px;}
.btn-next:disabled{opacity:.5;cursor:not-allowed;}
.type-grid{display:grid;grid-template-columns:1fr 1fr;gap:16px;margin-bottom:8px;}
.type-card{border:2px solid ${c.border};border-radius:14px;padding:20px;cursor:pointer;transition:all 150ms;position:relative;text-align:left;background:${c.raised};}
.type-card:hover{border-color:${c.accent};}
.type-card.selected{border-color:var(--tc-color,${c.accent});background:${c.accentDim};}
.summary-row{display:flex;justify-content:space-between;align-items:center;padding:8px 0;border-bottom:1px solid ${c.border};font-size:12.5px;}
.summary-row:last-child{border-bottom:none;}
@keyframes spin{to{transform:rotate(360deg)}}
`;

// =============================================================================
// FIELD COMPONENT — defined OUTSIDE the main component, so its identity is stable
// =============================================================================
function Fld({ label, required, hint, error, children }) {
  return (
    <div className="fld">
      <label>
        {label}
        {required && <span className="req"> *</span>}
      </label>
      {children}
      {hint && !error && <span className="fld-hint">{hint}</span>}
      {error && <span className="fld-err">⚠ {error}</span>}
    </div>
  );
}

function Ico({ d, s = 13 }) {
  return (
    <svg
      viewBox="0 0 24 24"
      width={s}
      height={s}
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    >
      <path d={d} />
    </svg>
  );
}

// =============================================================================
// MAIN COMPONENT
// =============================================================================
export default function RegisterFournisseur() {
  const dark = isDarkTheme(useThemeStore((s) => s.theme));
  const navigate = useNavigate();
  const c = T[dark ? "dark" : "light"];
  const styles = useMemo(() => css(c), [c]);

  const [step, setStep] = useState(1);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState({});
  const [globalError, setGlobalError] = useState("");
  const [showPw, setShowPw] = useState(false);

  const [form, setForm] = useState({
    type_compte: "FOURNISSEUR",
    raison_sociale: "",
    nom_commercial: "",
    adresse: "",
    wilaya: "",
    telephone: "",
    registre_commerce_num: "",
    numero_agrement: "",
    rib: "",
    nif: "",
    prenom: "",
    nom: "",
    email: "",
    password: "",
    password_confirm: "",
  });

  const [files, setFiles] = useState({
    registre_commerce: null,
    agrement: null,
    rib: null,
    carte_fiscale: null,
  });

  // Stable refs — never recreated
  const registre_commerce_ref = useRef();
  const agrement_ref = useRef();
  const rib_ref = useRef();
  const carte_fiscale_ref = useRef();
  const fileRefs = {
    registre_commerce: registre_commerce_ref,
    agrement: agrement_ref,
    rib: rib_ref,
    carte_fiscale: carte_fiscale_ref,
  };

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));
  const setFile = (key, file) => setFiles((f) => ({ ...f, [key]: file }));

  const validate = (s) => {
    const e = {};
    if (s === 2) {
      if (!form.raison_sociale.trim()) e.raison_sociale = "Requis";
      if (!form.adresse.trim()) e.adresse = "Requis";
      if (!form.wilaya) e.wilaya = "Requis";
    }
    if (s === 3) {
      if (!form.prenom.trim()) e.prenom = "Requis";
      if (!form.nom.trim()) e.nom = "Requis";
      if (!form.email.trim() || !/^[^@]+@[^@]+\.[^@]+$/.test(form.email))
        e.email = "Email invalide";
      if (form.password.length < 8) e.password = "8 caractères minimum";
      if (form.password !== form.password_confirm)
        e.password_confirm = "Les mots de passe ne correspondent pas";
    }
    if (s === 4) {
      if (!files.registre_commerce) e.registre_commerce = "Document requis";
      if (!files.agrement) e.agrement = "Document requis";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (validate(step)) {
      setErrors({});
      setStep((s) => s + 1);
    }
  };
  const back = () => {
    setErrors({});
    setStep((s) => s - 1);
  };

  const handleSubmit = async () => {
    if (!validate(4)) return;
    setSubmitting(true);
    setGlobalError("");
    try {
      const fd = new FormData();
      Object.entries(form).forEach(([k, v]) => {
        if (k !== "password_confirm") fd.append(k, v);
      });
      Object.entries(files).forEach(([k, v]) => {
        if (v) fd.append(k, v);
      });
      await authApi.registerFournisseur(fd);
      setSubmitted(true);
      setStep(5);
    } catch (e) {
      const msg =
        e?.response?.data?.message ??
        e.message ??
        "Une erreur est survenue. Veuillez réessayer.";
      setGlobalError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  // ==========================================================================
  // RENDER
  // All step content is inlined here — NO inner component functions.
  // Defining Step1/Step2/… as functions inside this body would make React
  // treat them as new component types on every re-render, unmounting/remounting
  // the DOM and destroying input focus after every keystroke.
  // ==========================================================================
  return (
    <div className="rf-shell">
      <style>{styles}</style>

      {/* ── Top bar ── */}
      <header className="rf-top">
        <div className="rf-brand">
          <div
            style={{
              width: 34,
              height: 34,
              borderRadius: 9,
              background: c.raised,
              border: `1px solid ${c.borderHi}`,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
            }}
          >
            <svg
              viewBox="0 0 24 24"
              width={18}
              height={18}
              fill="none"
              stroke={c.accent}
              strokeWidth="2"
            >
              <path
                d="M12 2L2 7l10 5 10-5-10-5M2 17l10 5 10-5M2 12l10 5 10-5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </div>
          <span
            style={{
              fontSize: 16,
              fontWeight: 800,
              color: c.t0,
              letterSpacing: "-.4px",
            }}
          >
            PHARMAKON <span style={{ color: c.t3, fontWeight: 400 }}>›</span>{" "}
            Inscription Fournisseur
          </span>
        </div>
        <Link
          to="/login"
          style={{
            fontSize: 12,
            color: c.t2,
            textDecoration: "none",
            padding: "7px 14px",
            border: `1px solid ${c.border}`,
            borderRadius: 8,
            fontWeight: 600,
            fontFamily: "'Outfit',sans-serif",
          }}
        >
          Déjà un compte ? Connexion
        </Link>
      </header>

      {/* ── Body ── */}
      <div className="rf-body">
        {/* ── Sidebar ── */}
        <aside className="rf-sidebar">
          {CFG.STEPS.map((s) => {
            const status =
              step > s.id ? "done" : step === s.id ? "active" : "todo";
            return (
              <div
                key={s.id}
                className="rf-step-item"
                style={{ cursor: s.id < step ? "pointer" : "default" }}
                onClick={() => {
                  if (s.id < step) setStep(s.id);
                }}
              >
                <div className={`step-dot ${status}`}>
                  {status === "done" ? (
                    <Ico d={ICONS.check} s={13} />
                  ) : (
                    <Ico d={s.icon} s={13} />
                  )}
                </div>
                <span className={`step-lbl ${status}`}>{s.label}</span>
              </div>
            );
          })}
          <div
            style={{
              marginTop: 32,
              padding: 12,
              background: c.raised,
              borderRadius: 10,
              border: `1px solid ${c.border}`,
            }}
          >
            <div
              style={{
                fontSize: 10,
                fontWeight: 700,
                color: c.accent,
                marginBottom: 6,
              }}
            >
              ℹ PROCESSUS
            </div>
            <div style={{ fontSize: 10.5, color: c.t2, lineHeight: 1.7 }}>
              Votre dossier sera examiné par notre équipe sous{" "}
              <strong style={{ color: c.t1 }}>48h ouvrées</strong>. Vous
              recevrez un email de confirmation.
            </div>
          </div>
        </aside>

        {/* ── Main card ── */}
        <main className="rf-main">
          <div className="rf-card">
            {/* ── STEP 1: Type selector ── */}
            {step === 1 && (
              <div>
                <div className="rf-card-title">Type de compte</div>
                <div className="rf-card-sub">
                  Sélectionnez votre profil pour commencer l'inscription
                </div>
                <div className="type-grid">
                  {CFG.ACCOUNT_TYPES.map((t) => (
                    <button
                      type="button"
                      key={t.id}
                      className={`type-card ${form.type_compte === t.id ? "selected" : ""}`}
                      style={{ "--tc-color": t.color }}
                      onClick={() => set({ type_compte: t.id })}
                    >
                      {t.badge && (
                        <span
                          style={{
                            position: "absolute",
                            top: 10,
                            right: 10,
                            fontSize: 9,
                            fontWeight: 800,
                            padding: "2px 7px",
                            borderRadius: 99,
                            background: t.color,
                            color: "#fff",
                          }}
                        >
                          {t.badge}
                        </span>
                      )}
                      <div style={{ fontSize: 32, marginBottom: 12 }}>
                        {t.icon}
                      </div>
                      <div
                        style={{
                          fontSize: 14,
                          fontWeight: 800,
                          color: c.t0,
                          marginBottom: 6,
                        }}
                      >
                        {t.label}
                      </div>
                      <div
                        style={{ fontSize: 11.5, color: c.t2, lineHeight: 1.5 }}
                      >
                        {t.desc}
                      </div>
                      <div
                        style={{
                          marginTop: 12,
                          width: 20,
                          height: 20,
                          borderRadius: "50%",
                          border: `2px solid ${form.type_compte === t.id ? t.color : c.border}`,
                          background:
                            form.type_compte === t.id ? t.color : "transparent",
                          display: "flex",
                          alignItems: "center",
                          justifyContent: "center",
                          transition: "all 150ms",
                        }}
                      >
                        {form.type_compte === t.id && (
                          <svg
                            viewBox="0 0 24 24"
                            width={11}
                            height={11}
                            fill="none"
                            stroke="#fff"
                            strokeWidth="3"
                          >
                            <path
                              d="M20 6L9 17l-5-5"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            />
                          </svg>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
                <p
                  style={{
                    fontSize: 11,
                    color: c.t3,
                    marginTop: 8,
                    textAlign: "center",
                  }}
                >
                  Vous pouvez modifier ce choix à l'étape suivante
                </p>
              </div>
            )}

            {/* ── STEP 2: Company info ── */}
            {step === 2 && (
              <div>
                <div className="rf-card-title">Informations entreprise</div>
                <div className="rf-card-sub">
                  Identité légale de votre entreprise
                </div>

                <div className="frow">
                  <Fld
                    label="Raison sociale"
                    required
                    error={errors.raison_sociale}
                  >
                    <input
                      className={`inp${errors.raison_sociale ? " err" : ""}`}
                      value={form.raison_sociale}
                      onChange={(e) => set({ raison_sociale: e.target.value })}
                      placeholder="ex: PHARMA DIST SARL"
                    />
                  </Fld>
                  <Fld
                    label="Nom commercial"
                    hint="Si différent de la raison sociale"
                  >
                    <input
                      className="inp"
                      value={form.nom_commercial}
                      onChange={(e) => set({ nom_commercial: e.target.value })}
                      placeholder="ex: PHARMADIST"
                    />
                  </Fld>
                </div>

                <div className="frow one">
                  <Fld label="Adresse complète" required error={errors.adresse}>
                    <input
                      className={`inp${errors.adresse ? " err" : ""}`}
                      value={form.adresse}
                      onChange={(e) => set({ adresse: e.target.value })}
                      placeholder="Zone industrielle, Rue, N°..."
                    />
                  </Fld>
                </div>

                <div className="frow">
                  <Fld label="Wilaya" required error={errors.wilaya}>
                    <select
                      className={`inp${errors.wilaya ? " err" : ""}`}
                      value={form.wilaya}
                      onChange={(e) => set({ wilaya: e.target.value })}
                    >
                      <option value="">Sélectionner...</option>
                      {CFG.WILAYAS.map((w, i) => (
                        <option key={w} value={w}>
                          {String(i + 1).padStart(2, "0")} — {w}
                        </option>
                      ))}
                    </select>
                  </Fld>
                  <Fld label="Téléphone" hint="Ligne professionnelle">
                    <input
                      className="inp"
                      value={form.telephone}
                      onChange={(e) => set({ telephone: e.target.value })}
                      placeholder="023 XX XX XX"
                    />
                  </Fld>
                </div>

                <div
                  style={{
                    borderTop: `1px solid ${c.border}`,
                    paddingTop: 18,
                    marginTop: 4,
                    marginBottom: 16,
                  }}
                >
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 800,
                      color: c.t3,
                      textTransform: "uppercase",
                      letterSpacing: ".7px",
                      marginBottom: 12,
                    }}
                  >
                    Identifiants légaux (optionnels — à compléter dans les
                    documents)
                  </div>
                  <div className="frow">
                    <Fld
                      label="N° Registre de Commerce"
                      hint="ex: 16/00-0000000 B 00"
                    >
                      <input
                        className="inp"
                        value={form.registre_commerce_num}
                        onChange={(e) =>
                          set({ registre_commerce_num: e.target.value })
                        }
                        placeholder="16/00-0000000 B 00"
                        style={{
                          fontFamily: "'JetBrains Mono',monospace",
                          fontSize: 12,
                        }}
                      />
                    </Fld>
                    <Fld
                      label="N° Agrément Distribution"
                      hint="MOH Distribution Pharmaceutique"
                    >
                      <input
                        className="inp"
                        value={form.numero_agrement}
                        onChange={(e) =>
                          set({ numero_agrement: e.target.value })
                        }
                        placeholder="AGR-DZ-XXXX"
                        style={{
                          fontFamily: "'JetBrains Mono',monospace",
                          fontSize: 12,
                        }}
                      />
                    </Fld>
                  </div>
                  <div className="frow">
                    <Fld label="RIB / IBAN" hint="Relevé d'identité bancaire">
                      <input
                        className="inp"
                        value={form.rib}
                        onChange={(e) => set({ rib: e.target.value })}
                        placeholder="DZ59 0000 0000 0000 0000 0000"
                        style={{
                          fontFamily: "'JetBrains Mono',monospace",
                          fontSize: 12,
                        }}
                      />
                    </Fld>
                    <Fld label="NIF" hint="Numéro d'identification fiscale">
                      <input
                        className="inp"
                        value={form.nif}
                        onChange={(e) => set({ nif: e.target.value })}
                        placeholder="000000000000000"
                        style={{
                          fontFamily: "'JetBrains Mono',monospace",
                          fontSize: 12,
                        }}
                      />
                    </Fld>
                  </div>
                </div>
              </div>
            )}

            {/* ── STEP 3: Admin account ── */}
            {step === 3 && (
              <div>
                <div className="rf-card-title">Compte administrateur</div>
                <div className="rf-card-sub">
                  Ce compte sera le gestionnaire principal du portail
                  fournisseur
                </div>

                <div className="frow">
                  <Fld label="Prénom" required error={errors.prenom}>
                    <input
                      className={`inp${errors.prenom ? " err" : ""}`}
                      value={form.prenom}
                      onChange={(e) => set({ prenom: e.target.value })}
                      placeholder="Prénom"
                    />
                  </Fld>
                  <Fld label="Nom" required error={errors.nom}>
                    <input
                      className={`inp${errors.nom ? " err" : ""}`}
                      value={form.nom}
                      onChange={(e) => set({ nom: e.target.value })}
                      placeholder="Nom de famille"
                    />
                  </Fld>
                </div>

                <div className="frow one">
                  <Fld
                    label="Email professionnel"
                    required
                    error={errors.email}
                  >
                    <input
                      type="email"
                      className={`inp${errors.email ? " err" : ""}`}
                      value={form.email}
                      onChange={(e) => set({ email: e.target.value })}
                      placeholder="admin@votreentreprise.dz"
                    />
                  </Fld>
                </div>

                <div className="frow">
                  <Fld
                    label="Mot de passe"
                    required
                    error={errors.password}
                    hint="8 caractères minimum"
                  >
                    <div style={{ position: "relative" }}>
                      <input
                        type={showPw ? "text" : "password"}
                        className={`inp${errors.password ? " err" : ""}`}
                        value={form.password}
                        onChange={(e) => set({ password: e.target.value })}
                        placeholder="••••••••"
                        style={{ paddingRight: 38 }}
                      />
                      <button
                        type="button"
                        onClick={() => setShowPw((v) => !v)}
                        style={{
                          position: "absolute",
                          right: 10,
                          top: "50%",
                          transform: "translateY(-50%)",
                          background: "none",
                          border: "none",
                          cursor: "pointer",
                          color: c.t2,
                          fontSize: 14,
                          padding: 0,
                        }}
                      >
                        {showPw ? "🙈" : "👁"}
                      </button>
                    </div>
                  </Fld>
                  <Fld
                    label="Confirmer le mot de passe"
                    required
                    error={errors.password_confirm}
                  >
                    <input
                      type="password"
                      className={`inp${errors.password_confirm ? " err" : ""}`}
                      value={form.password_confirm}
                      onChange={(e) =>
                        set({ password_confirm: e.target.value })
                      }
                      placeholder="••••••••"
                    />
                  </Fld>
                </div>

                <div
                  style={{
                    padding: "12px 14px",
                    background: c.raised,
                    borderRadius: 10,
                    border: `1px solid ${c.border}`,
                    fontSize: 11.5,
                    color: c.t2,
                    lineHeight: 1.6,
                  }}
                >
                  🔒 Votre compte sera activé uniquement après validation de
                  votre dossier par notre équipe. Vous recevrez un email de
                  confirmation.
                </div>
              </div>
            )}

            {/* ── STEP 4: Documents ── */}
            {step === 4 && (
              <div>
                <div className="rf-card-title">Documents justificatifs</div>
                <div className="rf-card-sub">
                  Uploadez les documents nécessaires à la validation de votre
                  dossier
                </div>
                <div className="doc-grid">
                  {CFG.REQUIRED_DOCS.map((doc) => {
                    const file = files[doc.key];
                    const hasErr = errors[doc.key];
                    return (
                      <div
                        key={doc.key}
                        className={`doc-card ${file ? "uploaded" : ""} ${hasErr ? "required-missing" : ""}`}
                        onClick={() => fileRefs[doc.key]?.current?.click()}
                      >
                        <input
                          type="file"
                          ref={fileRefs[doc.key]}
                          style={{ display: "none" }}
                          accept=".pdf,.jpg,.jpeg,.png"
                          onChange={(e) => {
                            const f = e.target.files?.[0];
                            if (f) {
                              setFile(doc.key, f);
                              setErrors((err) => ({ ...err, [doc.key]: "" }));
                            }
                          }}
                        />
                        <div
                          style={{
                            display: "flex",
                            alignItems: "flex-start",
                            gap: 10,
                          }}
                        >
                          <span style={{ fontSize: 24, flexShrink: 0 }}>
                            {doc.icon}
                          </span>
                          <div style={{ flex: 1, minWidth: 0 }}>
                            <div
                              style={{
                                fontSize: 12,
                                fontWeight: 700,
                                color: c.t0,
                                marginBottom: 3,
                              }}
                            >
                              {doc.label}
                              {doc.required && (
                                <span
                                  style={{ color: c.accent, marginLeft: 4 }}
                                >
                                  *
                                </span>
                              )}
                              {!doc.required && (
                                <span
                                  style={{
                                    fontSize: 9,
                                    color: c.t3,
                                    marginLeft: 6,
                                    padding: "1px 5px",
                                    background: c.raised,
                                    borderRadius: 3,
                                    fontWeight: 700,
                                    textTransform: "uppercase",
                                  }}
                                >
                                  Optionnel
                                </span>
                              )}
                            </div>
                            <div
                              style={{
                                fontSize: 10.5,
                                color: c.t2,
                                lineHeight: 1.5,
                              }}
                            >
                              {doc.hint}
                            </div>
                            {file ? (
                              <div
                                style={{
                                  marginTop: 8,
                                  display: "flex",
                                  alignItems: "center",
                                  gap: 5,
                                }}
                              >
                                <Ico d={ICONS.check} s={12} />
                                <span
                                  style={{
                                    fontSize: 10,
                                    color: c.accent,
                                    fontWeight: 700,
                                    overflow: "hidden",
                                    textOverflow: "ellipsis",
                                    whiteSpace: "nowrap",
                                    maxWidth: 140,
                                  }}
                                >
                                  {file.name}
                                </span>
                              </div>
                            ) : (
                              <div
                                style={{
                                  marginTop: 8,
                                  fontSize: 10,
                                  color: c.t3,
                                }}
                              >
                                PDF, JPG ou PNG · max 5 MB
                              </div>
                            )}
                            {hasErr && (
                              <div
                                style={{
                                  fontSize: 10,
                                  color: c.red,
                                  marginTop: 4,
                                  fontWeight: 600,
                                }}
                              >
                                ⚠ {hasErr}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
                <div
                  style={{
                    padding: "11px 14px",
                    background: "#fef9c3",
                    border: "1px solid #fde047",
                    borderRadius: 9,
                    fontSize: 11,
                    color: "#713f12",
                    lineHeight: 1.6,
                  }}
                >
                  ⚠️ Les documents uploadés seront vérifiés par notre équipe de
                  conformité. Votre compte sera activé sous 24-48 heures
                  ouvrables après validation.
                </div>
              </div>
            )}

            {/* ── STEP 5: Summary / Success ── */}
            {step === 5 && (
              <div>
                {submitted ? (
                  <div style={{ textAlign: "center", padding: "20px 0" }}>
                    <div
                      style={{
                        width: 70,
                        height: 70,
                        borderRadius: "50%",
                        background: c.accentDim,
                        border: `2px solid ${c.accent}`,
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        margin: "0 auto 20px",
                        fontSize: 30,
                      }}
                    >
                      ✅
                    </div>
                    <div
                      style={{
                        fontSize: 20,
                        fontWeight: 800,
                        color: c.t0,
                        marginBottom: 8,
                      }}
                    >
                      Dossier soumis avec succès !
                    </div>
                    <div
                      style={{
                        fontSize: 13,
                        color: c.t2,
                        lineHeight: 1.7,
                        maxWidth: 440,
                        margin: "0 auto 24px",
                      }}
                    >
                      Votre demande d'inscription a bien été reçue. Notre équipe
                      va examiner votre dossier et vous contactera par email (
                      <strong>{form.email}</strong>) pour vous confirmer
                      l'activation.
                    </div>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: 8,
                        maxWidth: 320,
                        margin: "0 auto",
                      }}
                    >
                      {[
                        "📋 Dossier reçu et en cours d'examen",
                        "⏱ Validation sous 24-48h ouvrables",
                        "📧 Email de confirmation à l'activation",
                        "🚀 Accès immédiat au portail après activation",
                      ].map((item, i) => (
                        <div
                          key={i}
                          style={{
                            padding: "10px 14px",
                            background: c.raised,
                            border: `1px solid ${c.border}`,
                            borderRadius: 9,
                            fontSize: 12,
                            color: c.t1,
                            textAlign: "left",
                          }}
                        >
                          {item}
                        </div>
                      ))}
                    </div>
                    <button
                      onClick={() => navigate("/login")}
                      style={{
                        marginTop: 28,
                        padding: "11px 28px",
                        borderRadius: 10,
                        border: "none",
                        background: c.accent,
                        color: "#fff",
                        fontSize: 13,
                        fontWeight: 700,
                        cursor: "pointer",
                        fontFamily: "'Outfit',sans-serif",
                      }}
                    >
                      Retour à la connexion
                    </button>
                  </div>
                ) : (
                  <div>
                    <div className="rf-card-title">Récapitulatif</div>
                    <div className="rf-card-sub">
                      Vérifiez vos informations avant envoi
                    </div>
                    {[
                      [
                        "Type de compte",
                        form.type_compte === "LABORATOIRE"
                          ? "🔬 Laboratoire Pharmaceutique"
                          : "🚚 Grossiste / Distributeur",
                      ],
                      ["Raison sociale", form.raison_sociale],
                      ["Wilaya", form.wilaya],
                      ["Adresse", form.adresse],
                      ["Téléphone", form.telephone || "—"],
                      ["Responsable", `${form.prenom} ${form.nom}`],
                      ["Email", form.email],
                      ["N° RC", form.registre_commerce_num || "—"],
                      ["N° Agrément", form.numero_agrement || "—"],
                      [
                        "Documents",
                        Object.values(files).filter(Boolean).length +
                          " / 4 uploadés",
                      ],
                    ].map(([k, v]) => (
                      <div key={k} className="summary-row">
                        <span style={{ color: c.t3, fontSize: 12 }}>{k}</span>
                        <span
                          style={{
                            color: c.t0,
                            fontSize: 12.5,
                            fontWeight: 600,
                          }}
                        >
                          {v}
                        </span>
                      </div>
                    ))}
                    {globalError && (
                      <div
                        style={{
                          marginTop: 16,
                          padding: "10px 14px",
                          background: c.redDim,
                          border: `1px solid ${c.red}33`,
                          borderRadius: 9,
                          color: c.red,
                          fontSize: 12,
                        }}
                      >
                        ⚠ {globalError}
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}

            {/* ── Navigation ── */}
            {!(step === 5 && submitted) && (
              <div className="rf-actions">
                <div>
                  {step > 1 && (
                    <button type="button" className="btn-back" onClick={back}>
                      ← Retour
                    </button>
                  )}
                  {step === 1 && (
                    <Link
                      to="/register"
                      style={{
                        fontSize: 12,
                        color: c.t2,
                        textDecoration: "none",
                        padding: "7px 14px",
                        border: `1px solid ${c.border}`,
                        borderRadius: 8,
                        fontWeight: 600,
                        fontFamily: "'Outfit',sans-serif",
                        display: "inline-block",
                      }}
                    >
                      ← Changer de type
                    </Link>
                  )}
                </div>
                {step < 5 && (
                  <button className="btn-next" onClick={next}>
                    Continuer →
                  </button>
                )}
                {step === 5 && !submitted && (
                  <button
                    type="button"
                    className="btn-next"
                    onClick={handleSubmit}
                    disabled={submitting}
                  >
                    {submitting ? (
                      <>
                        <svg
                          style={{ animation: "spin .9s linear infinite" }}
                          viewBox="0 0 24 24"
                          width={14}
                          height={14}
                          fill="none"
                          stroke="currentColor"
                          strokeWidth="2.5"
                        >
                          <path d="M21 12a9 9 0 11-6.219-8.56" />
                        </svg>
                        Envoi...
                      </>
                    ) : (
                      <>Soumettre le dossier →</>
                    )}
                  </button>
                )}
              </div>
            )}
          </div>
        </main>
      </div>
    </div>
  );
}
